/*!
 * @inniti/vue-horizontal-scroller
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2020-present, inniti Software Engineeering & Consulting
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import Vue from 'vue';

var script = Vue.extend({
  name: 'NNScrollerArrow',
  props: {
    direction: {
      type: String,
      default: 'right',

      validator(value) {
        return ['right', 'left'].includes(value);
      }

    }
  },
  computed: {
    path() {
      switch (this.direction) {
        case 'left':
          return 'M 22 2 L 2 18 L 22 34';

        case 'right':
        default:
          return 'M 2 2 L 22 18 L 2 34';
      }
    }

  }
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;
/* template */

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "nn-horizontal-scroller-arrow"
  }, [_vm._t("default", [_c('svg', {
    attrs: {
      "height": "20",
      "viewBox": "0 0 24 36",
      "width": "20"
    }
  }, [_c('path', {
    attrs: {
      "d": _vm.path,
      "fill": "none",
      "stroke": "currentColor",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "stroke-width": "4"
    }
  })])])], 2);
};

var __vue_staticRenderFns__ = [];
/* style */

const __vue_inject_styles__ = undefined;
/* scoped */

const __vue_scope_id__ = undefined;
/* module identifier */

const __vue_module_identifier__ = undefined;
/* functional template */

const __vue_is_functional_template__ = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, undefined, undefined, undefined);

let ro; // eslint-disable-next-line @typescript-eslint/ban-types

function debounce(fn, delay) {
  let timeout;
  return function () {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctx = this;
    const args = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(function () {
      fn.apply(ctx, args);
    }, delay);
  };
}

const defaultScrollDelay = 60;
var script$1 = Vue.extend({
  name: 'NNHorizontalScroller',
  components: {
    Arrow: __vue_component__
  },
  props: {
    arrows: Boolean,
    scrollOffset: {
      type: Number,
      default: 0
    },
    scrollDistance: {
      type: Number,
      default: NaN
    },
    scrollDelay: {
      type: Number,
      default: defaultScrollDelay
    }
  },

  data() {
    return {
      scrolling: false,
      atStart: true,
      atEnd: false,
      slideOffsets: [],
      scrollingTimeout: null
    };
  },

  computed: {},

  mounted() {
    ro = new ResizeObserver(this.onFrameResize);
    ro.observe(this.$refs.frame);
    this.calculateSlideOffsets();
  },

  beforeDestroy() {
    ro && ro.disconnect();
  },

  methods: {
    slideForwards() {
      const frame = this.$refs.frame;
      const distance = this.scrollDistance || frame.getBoundingClientRect().width;
      frame.scroll({
        left: Math.min(frame.scrollLeft + distance, frame.scrollWidth),
        behavior: 'smooth'
      });
    },

    slideBackwards() {
      const frame = this.$refs.frame;
      const distance = this.scrollDistance || frame.getBoundingClientRect().width;
      frame.scroll({
        left: Math.max(0, frame.scrollLeft - distance),
        behavior: 'smooth'
      });
    },

    onFrameResize: debounce(function () {
      this.calculateSlideOffsets();
      this.calculateStartEnd();
      this.$emit('resize', {
        position: this.$refs.frame.scrollLeft,
        atStart: this.atStart,
        atEnd: this.atEnd,
        width: this.$refs.frame.getBoundingClientRect().width
      });
    }, 60),

    calculateSlideOffsets() {
      const frame = this.$refs.frame;
      const currentPosition = frame.scrollLeft;
      frame.scrollLeft = 0;
      const frameBounds = frame.getBoundingClientRect();
      const slides = frame.querySelectorAll('.nn-horizontal-scroller-slide');
      const offsets = new Array(slides.length);
      let offset = 0;

      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        const slideStyle = window.getComputedStyle(slide);
        const margin = parseFloat(slideStyle.marginLeft) + parseFloat(slideStyle.marginRight);
        const slideWidth = slide.getBoundingClientRect().width + margin;
        offsets[i] = offset - frameBounds.x;
        offset += slideWidth;
      }

      this.slideOffsets = offsets;
      frame.scrollLeft = currentPosition;
    },

    slideTo(index) {
      const slides = this.$el.querySelectorAll('.nn-horizontal-scroller-slide');
      const frame = this.$refs.frame;
      const frameBounds = frame.getBoundingClientRect();

      if (index <= slides.length - 1 && index >= 0) {
        frame.scroll({
          left: this.slideOffsets[index] + frameBounds.x + this.scrollOffset,
          behavior: 'smooth'
        });
      } else if (index >= 0) {
        console.warn(`tried to slide to ${index}, but there's no slide at this index`);
      }
    },

    onScroll() {
      this.scrolling = true;
      this.scrollingTimeout && clearTimeout(this.scrollingTimeout);
      this.scrollingTimeout = null;
      this.scrollingTimeout = setTimeout(() => {
        this.calculateStartEnd();
        this.scrolling = false;
        let frame = this.$refs.frame;
        this.$emit('scroll', {
          position: frame.scrollLeft,
          atStart: this.atStart,
          atEnd: this.atEnd,
          width: frame.getBoundingClientRect().width
        });
      }, isNaN(this.scrollDelay) ? defaultScrollDelay : this.scrollDelay);
    },

    calculateStartEnd() {
      const frame = this.$refs.frame;
      this.atStart = frame.scrollLeft === 0;
      const frameBounds = frame.getBoundingClientRect();
      this.atEnd = frame.scrollLeft >= frame.scrollWidth - frameBounds.width;
    }

  }
});

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$1 = script$1;
/* template */

var __vue_render__$1 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "nn-horizontal-scroller"
  }, [_c('div', {
    ref: "frame",
    staticClass: "nn-horizontal-scroller__frame",
    on: {
      "scroll": _vm.onScroll
    }
  }, [_vm._t("default")], 2), _vm._v(" "), _vm.arrows ? _c('Arrow', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: !_vm.atStart,
      expression: "!atStart"
    }],
    staticClass: "nn-horizontal-scroller__arrow nn-horizontal-scroller__arrow--prev",
    attrs: {
      "direction": "left",
      "tabindex": "0"
    },
    nativeOn: {
      "click": function ($event) {
        return _vm.slideBackwards($event);
      },
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }

        return _vm.slideBackwards($event);
      }
    }
  }, [_vm._t("arrow-left")], 2) : _vm._e(), _vm._v(" "), _vm.arrows ? _c('Arrow', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: !_vm.atEnd,
      expression: "!atEnd"
    }],
    staticClass: "nn-horizontal-scroller__arrow nn-horizontal-scroller__arrow--next",
    attrs: {
      "direction": "right",
      "tabindex": "0"
    },
    nativeOn: {
      "click": function ($event) {
        return _vm.slideForwards($event);
      },
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }

        return _vm.slideForwards($event);
      }
    }
  }, [_vm._t("arrow-right")], 2) : _vm._e()], 1);
};

var __vue_staticRenderFns__$1 = [];
/* style */

const __vue_inject_styles__$1 = function (inject) {
  if (!inject) return;
  inject("data-v-987ab01e_0", {
    source: ".nn-horizontal-scroller{position:relative}.nn-horizontal-scroller__frame{scroll-behavior:smooth;scroll-snap-type:x mandatory;-ms-overflow-style:none;-ms-scroll-chaining:none;display:flex;-webkit-overflow-scrolling:touch;overflow-x:scroll;overscroll-behavior-x:none;width:100%;height:100%}.nn-horizontal-scroller__frame::-webkit-scrollbar{display:none}.nn-horizontal-scroller__arrow{position:absolute;top:50%;cursor:pointer;user-select:none;width:3rem;height:3rem;display:flex;justify-content:center;align-items:center;transform:translateY(-50%);border-radius:2px}.nn-horizontal-scroller__arrow--prev{left:0}.nn-horizontal-scroller__arrow--next{right:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$1 = undefined;
/* module identifier */

const __vue_module_identifier__$1 = undefined;
/* functional template */

const __vue_is_functional_template__$1 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, createInjector, undefined, undefined);

var script$2 = Vue.extend({
  name: 'NNHorizontalScrollerSlide'
});

/* script */
const __vue_script__$2 = script$2;
/* template */

var __vue_render__$2 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "nn-horizontal-scroller-slide"
  }, [_vm._t("default")], 2);
};

var __vue_staticRenderFns__$2 = [];
/* style */

const __vue_inject_styles__$2 = function (inject) {
  if (!inject) return;
  inject("data-v-14b30f17_0", {
    source: ".nn-horizontal-scroller-slide{scroll-snap-align:start;min-width:5rem;flex:0 0 auto;height:100%}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$2 = undefined;
/* module identifier */

const __vue_module_identifier__$2 = undefined;
/* functional template */

const __vue_is_functional_template__$2 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2,
  staticRenderFns: __vue_staticRenderFns__$2
}, __vue_inject_styles__$2, __vue_script__$2, __vue_scope_id__$2, __vue_is_functional_template__$2, __vue_module_identifier__$2, false, createInjector, undefined, undefined);

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Scroller: __vue_component__$1,
  ScrollerSlide: __vue_component__$2
});

// Import vue components
// eslint-disable-next-line @typescript-eslint/no-explicit-any

// install function executed by Vue.use()
const install = function (Vue) {
  if (install.installed) return;
  install.installed = true;
  Object.entries(components).forEach(([componentName, component]) => {
    Vue.component(`NNHorizontal${componentName}`, component);
  });
}; // Create module definition for Vue.use()


const plugin = {
  install
}; // To auto-install on non-es builds, when vue is found

export default plugin;
export { __vue_component__$1 as Scroller, __vue_component__$2 as ScrollerSlide };
